import { inject, Injectable } from '@angular/core';
import { AppPermission } from '../../../app-permission';
import { USER_VERIFIED_ROLE } from '@xt/domain-model';
import { combineLatest, map, Observable, of } from 'rxjs';
import { ContextNavigation } from './side-navigation.models';
import { UserInformationProviderContext } from '@xt/user-context';
import {
  CONTEXT_NAVIGATION_ITEMS,
  CONTEXT_NAVIGATION_STYLE_CONFIG,
} from './side-navigation.constants';
import { select, Store } from '@ngrx/store';
import {
  selectEmergencyUrl,
  selectInformationPortalUrl,
} from '@core/store/selectors/app-config.selectors';
import { userPartnerCodeSelector } from '@core/store/selectors/user-profile.selectors';
import { PermissionsService } from '@xt/core/permissions';
import { SideNavigationListItem } from '@xt/ui-kit';

@Injectable({
  providedIn: 'root',
})
export class SideNavigationConfig {
  private permissionService = inject(PermissionsService);
  private store = inject(Store);

  emergencyUrl: string = '';
  partnerCode: string = '';
  landingPage: string = '';

  constructor() {
    this.store.pipe(select(userPartnerCodeSelector)).subscribe((code) => {
      this.partnerCode = code;
    });

    // Initialize the observables
    this.initializeUrls();
  }

  private initializeUrls(): void {
    this.store.pipe(selectEmergencyUrl).subscribe((url) => {
      this.emergencyUrl = url;
      this.initializeBottomNavigationConfig();
    });

    this.store.pipe(selectInformationPortalUrl).subscribe((url) => {
      this.landingPage = url;
      this.initializeMainNavigationConfig();
    });
  }

  public mainNavigationConfig: SideNavigationListItem[] = [];
  public bottomNavigationConfig: SideNavigationListItem[] = [];

  // eslint-disable-next-line max-lines-per-function
  private initializeMainNavigationConfig(): void {
    this.mainNavigationConfig = [
      {
        url: this.landingPage,
        routerLinkActive: '',
        routerLinkActiveOptions: { exact: true },
        icon: 'language',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.publicArea',
        enabled$: of(true),
      },
      {
        routerLink: '/dashboard',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: true },
        icon: 'grid_view',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.dashboard',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.Dashboard,
        ),
      },
      {
        routerLink: '/dashboard/appointments',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: false },
        icon: 'today',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.appointments',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.Appointments,
        ),
      },
      {
        routerLink: '/dashboard/messages',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: false },
        icon: 'forum',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.messages',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.Messenger,
        ),
      },
      {
        routerLink: '/dashboard/documents',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: false },
        icon: 'description',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.files',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.Documents,
        ),
      },
      {
        routerLink: '/dashboard/questionnaires',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: false },
        icon: 'diagnosis',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.questionnaires',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.Questionnaires,
        ),
      },
      {
        routerLink: '/dashboard/notes',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: false },
        icon: 'event_note',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.notes',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.Notes,
        ),
      },
      {
        routerLink: '/dashboard/diary',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: false },
        icon: 'menu_book',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.diary',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.Diary,
        ),
      },
      {
        routerLink: '/dashboard/patients',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: true },
        icon: 'groups',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.patients',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.PatientList,
        ),
      },
      {
        routerLink: '/dashboard/representations',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: true },
        icon: 'data_loss_prevention',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.representations',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.Representations,
        ),
      },
      {
        routerLink: '/dashboard/monitoring',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: true },
        icon: 'visibility',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.monitoring',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.Monitoring,
        ),
      },
      {
        routerLink: '/dashboard/inactiveAccounts',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: true },
        icon: 'groups',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.inactiveAccounts',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.InactiveAccounts,
        ),
      },
      {
        routerLink: '/dashboard/documentation',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: true },
        icon: 'event_note',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.documentation',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.Documentation,
        ),
      },
      {
        routerLink: '/dashboard/reconciliation',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: true },
        icon: 'person_remove',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.reconciliation',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.Reconciliation,
        ),
      },
      {
        routerLink: '/dashboard/verification',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: true },
        icon: 'how_to_reg',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.verification',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.Verification,
        ),
      },
      {
        routerLink: '/dashboard/action-page/material-poolset',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: true },
        icon: 'note',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.materialpool',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.Materialpool,
        ),
      },
      {
        action: 'openPolavis',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: false },
        icon: 'add',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.polavis',
        enabled$: combineLatest([
          this.permissionService.hasPermission$(AppPermission.Polavis),
          of(this.partnerCode === '1002'),
        ]).pipe(
          map(
            ([hasPermission, isCorrectPartner]) =>
              hasPermission && isCorrectPartner,
          ),
        ),
      },
      {
        routerLink: '/dashboard/privacy-cockpit',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: false },
        icon: 'share',
        iconColor: 'white',
        iconTitle: 'app.profileQuickMenu.privacyCockpit',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.PrivacyCockpitSide,
        ),
      },
    ];
  }

  /* eslint-enable */
  private initializeBottomNavigationConfig(): void {
    this.bottomNavigationConfig = [
      {
        action: 'openFaqDialog',
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: false },
        icon: 'help',
        iconColor: 'white',
        iconTitle: 'app.sidemenu.help',
        enabled$: of(true),
      },
      {
        url: this.emergencyUrl,
        routerLinkActive: 'active',
        routerLinkActiveOptions: { exact: false },
        icon: 'healing',
        iconColor: 'red',
        iconTitle: 'app.sidemenu.emergency',
        enabled$: this.permissionService.hasPermission$(
          AppPermission.Emergency,
        ),
      },
    ];
  }

  public readonly providerPatientContextConfig = (
    context?: UserInformationProviderContext,
  ): ContextNavigation => {
    return {
      contextRouterLink: `patients`,
      role: USER_VERIFIED_ROLE.PROVIDER,
      style: CONTEXT_NAVIGATION_STYLE_CONFIG,
      context: context?.patient?.name,
      contextSelectedId: context?.patient?.id,
      items: [
        CONTEXT_NAVIGATION_ITEMS.MASTER_DATA,
        CONTEXT_NAVIGATION_ITEMS.DOCUMENTS,
        CONTEXT_NAVIGATION_ITEMS.LOGBOOK,
      ],
    };
  };

  public readonly representativePatientContextConfig = (
    context?: UserInformationProviderContext,
  ): ContextNavigation => {
    return {
      contextRouterLink: `representations`,
      style: CONTEXT_NAVIGATION_STYLE_CONFIG,
      context: context?.patient?.name,
      contextSelectedId: context?.patientRepresentation?.id,
      role: USER_VERIFIED_ROLE.REPRESENTATIVE,
      items: [
        CONTEXT_NAVIGATION_ITEMS.MASTER_DATA,
        CONTEXT_NAVIGATION_ITEMS.APPOINTMENTS,
        CONTEXT_NAVIGATION_ITEMS.DOCUMENTS,
        CONTEXT_NAVIGATION_ITEMS.LOGBOOK,
        CONTEXT_NAVIGATION_ITEMS.NOTES,
        CONTEXT_NAVIGATION_ITEMS.PERMISSIONS,
      ],
    };
  };

  private roleBasedSelectedContextNavigationConfigs = {
    [USER_VERIFIED_ROLE.PROVIDER]: this.providerPatientContextConfig(),
    [USER_VERIFIED_ROLE.REPRESENTATIVE]:
      this.representativePatientContextConfig(),
  };

  public readonly contextSelectedNavigationConfig = (
    config: ContextNavigation,
  ) => [
    {
      routerLink: `${config.contextRouterLink}/${config.contextSelectedId}/master-data`,
      routerLinkActive: 'active',
      routerLinkActiveOptions: { exact: false },
      icon: 'folder_supervised',
      iconColor: 'white',
      iconTitle: 'app.sidemenu.masterData',
      enabled$: this.checkRoleBasedNavigationItem$(
        config.role,
        CONTEXT_NAVIGATION_ITEMS.MASTER_DATA,
      ),
    },
    {
      routerLink: `${config.contextRouterLink}/${config.contextSelectedId}/appointments`,
      routerLinkActive: 'active',
      routerLinkActiveOptions: { exact: false },
      icon: 'today',
      iconColor: 'white',
      iconTitle: 'app.sidemenu.appointments',
      enabled$: this.checkRoleBasedNavigationItem$(
        config.role,
        CONTEXT_NAVIGATION_ITEMS.APPOINTMENTS,
      ),
    },
    {
      routerLink: `${config.contextRouterLink}/${config.contextSelectedId}/documents`,
      routerLinkActive: 'active',
      routerLinkActiveOptions: { exact: false },
      icon: 'description',
      iconColor: 'white',
      iconTitle: 'app.sidemenu.files',
      enabled$: this.checkRoleBasedNavigationItem$(
        config.role,
        CONTEXT_NAVIGATION_ITEMS.DOCUMENTS,
      ),
    },
    {
      routerLink: `${config.contextRouterLink}/${config.contextSelectedId}/logbook`,
      routerLinkActive: 'active',
      routerLinkActiveOptions: { exact: true },
      icon: 'list_alt',
      iconColor: 'white',
      iconTitle: 'app.sidemenu.logbook',
      enabled$: this.checkRoleBasedNavigationItem$(
        config.role,
        CONTEXT_NAVIGATION_ITEMS.LOGBOOK,
      ),
    },
    {
      routerLink: `${config.contextRouterLink}/${config.contextSelectedId}/notes`,
      routerLinkActive: 'active',
      routerLinkActiveOptions: { exact: true },
      icon: 'event_note',
      iconColor: 'white',
      iconTitle: 'app.sidemenu.notes',
      enabled$: this.checkRoleBasedNavigationItem$(
        config.role,
        CONTEXT_NAVIGATION_ITEMS.NOTES,
      ),
    },
    {
      routerLink: `${config.contextRouterLink}/${config.contextSelectedId}/privacy-cockpit`,
      routerLinkActive: 'active',
      routerLinkActiveOptions: { exact: false },
      icon: 'share',
      iconColor: 'white',
      iconTitle: 'app.sidemenu.privacyCockpit',
      enabled$: this.checkRoleBasedNavigationItem$(
        config.role,
        CONTEXT_NAVIGATION_ITEMS.PERMISSIONS,
      ),
    },
    {
      routerLink: `${config.contextRouterLink}/${config.contextSelectedId}/profile`,
      routerLinkActive: 'active',
      routerLinkActiveOptions: { exact: false },
      icon: 'settings',
      iconColor: 'white',
      iconTitle: 'app.sidemenu.settings',
      enabled$: this.checkRoleBasedNavigationItem$(
        config.role,
        CONTEXT_NAVIGATION_ITEMS.PERMISSIONS,
      ),
    },
  ];

  public readonly jumpingPointConfig = (
    jumpingPoint,
  ): SideNavigationListItem => {
    return {
      routerLinkActive: '',
      routerLinkActiveOptions: { exact: false },
      icon: 'exit_to_app',
      iconColor: 'white',
      iconTitle: jumpingPoint.display || '',
      enabled$: of(true),
      url: jumpingPoint.url,
    };
  };

  private checkRoleBasedNavigationItem$(
    role: USER_VERIFIED_ROLE,
    item: string,
  ): Observable<boolean> {
    return of(
      this.roleBasedSelectedContextNavigationConfigs[role].items.includes(item),
    );
  }
}
